import { z } from 'zod';
import { BARK_MCP_TOOLS } from './bark.js';
import { CALL_MCP_TOOLS } from './call.js'; // viko-call
import { Agent, Id, TimestampMs } from './common.js';
import { AskEvidenceInput, AskOptionInput, DECISION_MCP_TOOLS, DecisionAnswerMode, DecisionCategory, } from './decision.js'; // phase2-decisions
import { VikoInventoryInput, VikoInventoryOutput, VikoInventoryUpdateInput, VikoInventoryUpdateOutput, } from './inventory.js';
import { ModelId, ModelTier } from './model-profiles.js'; // g18
import { VikoTaskCreateBatchInput, VikoTaskCreateBatchOutput } from './plan-batch.js'; // g18-batch
// ---- triage-tools: phase 3 triage toolset + context sections (area file triage-tools.ts) ----
import { ContextAsk, ContextBark, ContextGoal, ContextLanding, ContextLane, TRIAGE_P3_MCP_TOOLS, } from './triage-tools.js';
import { VISUAL_REVIEW_MCP_TOOLS } from './visual-review.js'; // visual-review
export * from './triage-tools.js';
import { MACHINE_RESOURCE_MCP_TOOLS } from './machine-resources.js'; // machine-resources
// ---- end triage-tools ----
import { VOICE_MCP_TOOLS } from './voice.js'; // g8-voice
/**
 * Viko MCP server tool schemas (stdio shim spawned per run; token scoped to the run).
 * Inputs are what the agent sends; outputs are the structured content returned.
 */
const TaskStatus = z.enum([
    'proposed',
    'ready',
    'in_progress',
    'blocked',
    'review',
    'done',
    'dropped',
]);
const TaskSummary = z.object({
    id: Id,
    title: z.string(),
    status: TaskStatus,
    labels: z.array(z.string()).optional(),
    dependsOn: z.array(Id).optional(),
    acceptance: z.string().optional(),
});
const UsageWindow = z.object({
    agent: Agent,
    window: z.string(),
    usedPct: z.number().optional(),
    resetsAt: TimestampMs.optional(),
});
// --- viko_context -----------------------------------------------------------------------------
export const VikoContextInput = z.object({
    include: z
        .array(z.enum([
        'brief',
        'tasks',
        'rules',
        'runs',
        'usage',
        'policy',
        // ---- triage-tools: lanes/barks/asks/goal/landings (default for triage runs) ----
        'lanes',
        'barks',
        'asks',
        'goal',
        'landings',
        // ---- end triage-tools ----
    ]))
        .optional()
        .describe('Sections to include; default all (lanes/barks/asks/goal/landings: on request).'),
});
export const VikoContextOutput = z.object({
    project: z.object({ id: Id, name: z.string() }),
    workstream: z
        .object({
        id: Id,
        name: z.string(),
        branch: z.string().optional(),
        worktree: z.string().optional(),
        brief: z.string().optional(),
    })
        .optional(),
    currentTask: TaskSummary.optional(),
    tasks: z.array(TaskSummary).optional(),
    rules: z.array(z.object({ id: Id, text: z.string(), scope: z.string() })).optional(),
    /** Other live runs in this project only (metadata, never transcripts). */
    otherRuns: z
        .array(z.object({
        runId: Id,
        agent: Agent,
        machine: z.string(),
        workstream: z.string().optional(),
        task: z.string().optional(),
        status: z.string(),
        branch: z.string().optional(),
    }))
        .optional(),
    context: z
        .object({
        pct: z.number().optional(),
        tokens: z.number().optional(),
        window: z.number().optional(),
    })
        .optional(),
    usage: z.array(UsageWindow).optional(),
    /** Live efficiency hints from this run so far (daemon-local; at most 3, short). */
    efficiency: z.object({ hints: z.array(z.string().max(400)).max(3) }).optional(),
    policy: z
        .object({
        autonomy: z.string(),
        loopProfile: z.string(),
        /** Plain-language ask policy: proceed on reversible assumptions, ask only high-leverage questions. */
        askPolicy: z.string(),
    })
        .optional(),
    // ---- triage-tools: project-wide sections (metadata + short excerpts, this project only) ----
    lanes: z.array(ContextLane).optional(),
    barks: z.array(ContextBark).optional(),
    asks: z.array(ContextAsk).optional(),
    goal: ContextGoal.optional(),
    landings: z.array(ContextLanding).optional(),
    // ---- end triage-tools ----
});
// --- viko_ask ---------------------------------------------------------------------------------
export const VikoAskInput = z.object({
    question: z.string().min(1),
    header: z.string().max(40).optional(),
    // phase2-decisions: options may carry rationale, pros/cons, reversibility and a recommendation.
    options: z.array(AskOptionInput).max(8).optional(),
    multiSelect: z.boolean().optional(),
    /** Default false: the agent keeps working; the answer arrives as a message. */
    blocking: z.boolean().optional(),
    /** Reversible assumption the agent proceeds with until answered. */
    assumption: z.string().optional(),
    taskId: Id.optional(),
    // ---- phase2-decisions: structured decision fields (all optional) ----
    /** Why this needs the user (shown under the question). */
    why: z.string().max(4000).optional(),
    category: DecisionCategory.optional(),
    /** ≤4 options, no evidence needed, not risky: offer quick-pick. */
    simple: z.boolean().optional(),
    evidence: z.array(AskEvidenceInput).max(30).optional(),
    /** Tasks the answer unblocks (leverage). */
    unblocks: z.number().int().nonnegative().max(1000).optional(),
    // ---- end phase2-decisions ----
    // ---- triage-tools: triage asks are linked to the bark (outcome tree + undo withdraws) ----
    barkId: Id.optional(),
    // ---- end triage-tools ----
});
export const VikoAskOutput = z.object({
    questionId: Id,
    status: z.enum(['pending', 'answered', 'cancelled']),
    answer: z.array(z.string()).optional(),
    // ---- phase2-decisions ----
    /** The decision entity (thread, options, evidence) when the account is encrypted. */
    decisionId: Id.optional(),
    answerMode: DecisionAnswerMode.optional(),
    /** "Decide for me" guidance: pick, proceed, and report with `viko_decision_resolve`. */
    guidance: z.string().optional(),
    // ---- end phase2-decisions ----
});
// --- viko_task_update -------------------------------------------------------------------------
export const VikoTaskUpdateInput = z.object({
    taskId: Id,
    status: TaskStatus.exclude(['proposed']).optional(),
    note: z.string().optional(),
    /** 0–100 */
    progress: z.number().min(0).max(100).optional(),
    blockedReason: z.string().optional(),
    // ---- triage-tools: reprioritize, move, enrich (work runs: own task only) ----
    priority: z.number().int().min(0).max(10).optional(),
    rank: z.number().finite().optional(),
    workstreamId: Id.optional(),
    /** Replaces the acceptance criteria (one per line). */
    acceptance: z.string().max(4000).optional(),
    appendDescription: z.string().max(20_000).optional(),
    /** Triage: the bark this change comes from (recorded for undo). */
    barkId: Id.optional(),
    // ---- end triage-tools ----
});
export const VikoTaskUpdateOutput = z.object({ ok: z.boolean(), task: TaskSummary });
// --- viko_task_create -------------------------------------------------------------------------
export const VikoTaskCreateInput = z.object({
    title: z.string().min(1),
    description: z.string().optional(),
    workstreamId: Id.optional(),
    labels: z.array(z.string()).optional(),
    dependsOn: z.array(Id).optional(),
    acceptance: z.string().optional(),
    /** Why the agent proposes this (provenance shown to the user). */
    rationale: z.string(),
    // ---- bark-triage: triage-only fields (ignored/refused for work runs) ----
    /** The bark this task comes from; its text is embedded verbatim in the description. */
    barkId: Id.optional(),
    /** `ready` only when the project's autonomy allows (fast profile); else `proposed`. */
    start: z.enum(['ready', 'proposed']).optional(),
    rank: z.number().optional(),
    // ---- end bark-triage ----
    // g18: the planner's model choice for this task, with its reason
    tier: ModelTier.optional(),
    model: ModelId.optional(),
    modelReason: z.string().max(200).optional(),
});
/**
 * Work-run tasks are always `proposed` and never auto-dispatched; a triage run may get `ready`
 * on a fast-profile project (bark-triage).
 */
export const VikoTaskCreateOutput = z.object({
    taskId: Id,
    status: z.enum(['proposed', 'ready']),
    /** bark-triage: a work run started under the project's dispatch grant. */
    runId: Id.optional(),
});
// --- viko_post_artifact -----------------------------------------------------------------------
export const VikoPostArtifactInput = z.object({
    /** One file (or, with auto-artifacts, a directory or HTML page). Optional when `paths` is given. */
    path: z.string().min(1).optional(),
    title: z.string().optional(),
    kind: z.enum(['screenshot', 'video', 'image', 'document', 'build', 'log', 'other']).optional(),
    caption: z.string().optional(),
    taskId: Id.optional(),
    /** Compare/Pick group (e.g. several concept variants). */
    group: z.string().optional(),
    // ---- auto-artifacts: batches, galleries and A/B comparisons ----
    /** Several files/directories/HTML pages at once (braces and globs are not expanded here). */
    paths: z.array(z.string().min(1)).max(200).optional(),
    /** `comparison` lays A/B variants (e.g. `A-night.png`, `B-night.png`) side by side. */
    layout: z.enum(['auto', 'gallery', 'comparison']).optional(),
    /** Set by Viko's own hooks (`open`); agents omit it. */
    source: z.enum(['post', 'open']).optional(),
    // ---- end auto-artifacts ----
});
export const VikoPostArtifactOutput = z.object({
    artifactId: Id,
    url: z.string().optional(),
    // ---- auto-artifacts ----
    artifactIds: z.array(Id).optional(),
    /** One line for the agent: what was published (and what was skipped, and why). */
    note: z.string().optional(),
    skipped: z.array(z.object({ path: z.string(), reason: z.string() })).optional(),
    /** False when the user is away from this machine: a captured `open` need not run locally. */
    openLocally: z.boolean().optional(),
    // ---- end auto-artifacts ----
});
// --- viko_run_with_secret ---------------------------------------------------------------------
export const VikoRunWithSecretInput = z.object({
    /** Allowlisted `op://` reference; the value never enters agent context. */
    secretRef: z.string().regex(/^op:\/\//),
    /** Named command from the project's allowlist. */
    commandName: z.string().min(1),
    args: z.array(z.string()).optional(),
    cwd: z.string().optional(),
});
export const VikoRunWithSecretOutput = z.object({
    requestId: Id,
    status: z.enum(['pending_approval', 'ok', 'denied', 'failed']),
    exitCode: z.number().int().optional(),
    /** Exact-value scrubbed. */
    stdout: z.string().optional(),
    stderr: z.string().optional(),
});
// --- viko_request_landing ---------------------------------------------------------------------
export const VikoRequestLandingInput = z.object({
    summary: z.string().min(1),
    branch: z.string().optional(),
    taskIds: z.array(Id).optional(),
    target: z.enum(['main', 'pr']).optional(),
});
export const VikoRequestLandingOutput = z.object({
    landingId: Id,
    status: z.enum(['queued', 'awaiting_approval', 'rejected']),
    reason: z.string().optional(),
});
// --- viko_handoff -----------------------------------------------------------------------------
export const VikoHandoffInput = z.object({
    reason: z.enum(['context', 'scope_change', 'blocked', 'done', 'other']),
    summary: z.string().min(1),
    nextSteps: z.array(z.string()),
    openQuestions: z.array(z.string()).optional(),
    /** Files/paths the next run should read first. */
    keyFiles: z.array(z.string()).optional(),
});
export const VikoHandoffOutput = z.object({
    handoffId: Id,
    /** Run that will continue (may start after this run stops). */
    nextRunId: Id.optional(),
});
// --- viko_search_history ----------------------------------------------------------------------
export const VikoSearchHistoryInput = z.object({
    query: z.string().min(1),
    limit: z.number().int().min(1).max(50).optional(),
    kinds: z.array(z.string()).optional(),
    since: TimestampMs.optional(),
});
export const VikoSearchHistoryOutput = z.object({
    results: z.array(z.object({
        runId: Id,
        eventId: Id,
        seq: z.number().int().positive(),
        ts: TimestampMs,
        kind: z.string(),
        snippet: z.string(),
        score: z.number().optional(),
        workstream: z.string().optional(),
    })),
});
export const VIKO_MCP_TOOLS = {
    viko_context: { input: VikoContextInput, output: VikoContextOutput },
    viko_ask: { input: VikoAskInput, output: VikoAskOutput },
    viko_task_update: { input: VikoTaskUpdateInput, output: VikoTaskUpdateOutput },
    viko_task_create: { input: VikoTaskCreateInput, output: VikoTaskCreateOutput },
    // g18-batch: a whole plan (tasks + dependency graph), proposed in one call
    viko_task_create_batch: { input: VikoTaskCreateBatchInput, output: VikoTaskCreateBatchOutput },
    viko_post_artifact: { input: VikoPostArtifactInput, output: VikoPostArtifactOutput },
    viko_run_with_secret: { input: VikoRunWithSecretInput, output: VikoRunWithSecretOutput },
    viko_request_landing: { input: VikoRequestLandingInput, output: VikoRequestLandingOutput },
    viko_handoff: { input: VikoHandoffInput, output: VikoHandoffOutput },
    viko_search_history: { input: VikoSearchHistoryInput, output: VikoSearchHistoryOutput },
    // g10-inventory: per-project stack inventory (schemas in inventory.ts)
    viko_inventory: { input: VikoInventoryInput, output: VikoInventoryOutput },
    viko_inventory_update: { input: VikoInventoryUpdateInput, output: VikoInventoryUpdateOutput },
    // ---- bark-triage: triage tools (listed/callable for role 'triage' only) ----
    ...BARK_MCP_TOOLS,
    // ---- end bark-triage ----
    // ---- phase2-decisions: the asking agent replies in the thread / resolves a delegated ask ----
    ...DECISION_MCP_TOOLS,
    // ---- end phase2-decisions ----
    // ---- visual-review: design-option sets (A–D…) posted as one decision ----
    ...VISUAL_REVIEW_MCP_TOOLS,
    // ---- end visual-review ----
    // ---- triage-tools: phase 3 toolset ----
    ...TRIAGE_P3_MCP_TOOLS,
    // ---- end triage-tools ----
    // ---- g8-voice: research library (every role; scoped to the run's project) ----
    ...VOICE_MCP_TOOLS,
    // ---- end g8-voice ----
    // ---- machine-resources: leases (viko lock) and long-job waits (every role) ----
    ...MACHINE_RESOURCE_MCP_TOOLS,
    // ---- end machine-resources ----
    // ---- viko-call: call-session tools (role 'call' only; see call.ts) ----
    ...CALL_MCP_TOOLS,
    // ---- end viko-call ----
};
//# sourceMappingURL=mcp.js.map